import { startTransition, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryState } from 'nuqs';
import { AgentSidebar } from '@/components/agent/AgentSidebar';
import { AgentStreamView } from '@/components/agent/AgentStreamView';
import { AgentStreamErrorBoundary } from '@/components/agent/AgentStreamErrorBoundary';
import { AgentDashboard } from '@/components/agent/AgentDashboard';
import { AgentNoAccessState } from '@/components/agent/AgentNoAccessState';
import { AgentNotFoundState } from '@/components/agent/AgentNotFoundState';
import { LaunchAgentDialog } from '@/components/agent/launch-agent-dialog';
import { TwoPanelLayout } from '@reflex/ui/components/layout/TwoPanelLayout';
import { ErrorState } from '@reflex/ui/components/shared/ErrorState';
import { LoadingState } from '@reflex/ui/components/shared/LoadingState';
import { useIsDesktop } from '@reflex/ui/hooks/useIsDesktop';
import { useNavStyle } from '@/components/layout/NavBar/useNavStyle';
import { useNavPanel } from '@/components/layout/nav-panel-context';
import { useAgents, useAgent } from '@/hooks/agents/useAgents';
import { usePrefetchAgentStream } from '@/hooks/agents/useAgentStream';
import { useRecordAgentView, useAgentViews } from '@/hooks/agents/useAgentViews';
import { useLiveAgentTurnSignals } from '@/hooks/agents/useLiveAgentTurnSignals';
import { useAuth } from '@/hooks/auth/useAuth';
import { useAgentFilters } from '@/hooks/agents/useAgentFilters';
import { useAgentListCompletion } from '@/hooks/agents/useAgentListCompletion';
import { AgentListFilterContext } from '@/components/agent/agent-list-filter-context';
import { useTwoPanelState } from '@/hooks/ui/useTwoPanelState';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { getNonArchivedAgents, type SidebarSectionContext } from '@/lib/agent-utils';
import { shouldSkipAutoSelect } from '@/lib/agents-index-route-state';
import { ApiError } from '@/lib/api';
import type { Agent, AgentDraft } from '@/lib/types';
import { type PluginAttachmentValue } from '@runloop/reflex-contract';
import { LaunchAgentRouteStateSchema } from '@reflex/shared';
import {
  isLaunchDialogRequested,
  launchDialogDraftId,
  launchDialogGroupId,
  withLaunchDialogQuery,
  stripLaunchDialogQuery,
} from '@/pages/agents/launch-dialog-url';

/**
 * What the URL's agent id resolved to. The distinction the page has to keep
 * is between "no agent asked for" and "an agent was asked for and could not
 * be shown": both used to render the launch placeholder, which reads as an
 * empty workspace and hides a broken deep link (notably an id carried across
 * an org switch, which the server answers with a 404).
 */
type AgentLookup = 'none' | 'loading' | 'found' | 'noAccess' | 'notFound' | 'error';

function resolveAgentLookup(
  agent: Agent | null,
  error: unknown,
  fetching: boolean,
): Exclude<AgentLookup, 'none'> {
  if (agent) return 'found';
  if (error instanceof ApiError) {
    // 403: the run exists in this org but belongs to another user and no
    // grant covers it. 404: no such run here — either it never existed or it
    // belongs to the org the viewer just left.
    if (error.status === 403) return 'noAccess';
    if (error.status === 404) return 'notFound';
  }
  if (error) return 'error';
  // `fetching` deliberately means "a request is in flight", not TanStack's
  // `isPending`: a *disabled* query is also `isPending`, so keying off that
  // would spin forever on "Loading session…" the day `useAgent` grows a
  // second `enabled` condition. No row, no error, nothing in flight is a
  // dead end, not a wait.
  return fetching ? 'loading' : 'notFound';
}

export function AgentsPage() {
  const { user } = useAuth();
  // Same live read state the sidebar feeds the `Unread` facet, so the page's
  // own `filterAgents` use (default-selection + `listMatch`) stays in step with
  // the sidebar list instead of picking an agent the filter has hidden.
  const unreadViews = useAgentViews();
  const turnActivities = useLiveAgentTurnSignals();
  const agentFilters = useAgentFilters(user?.id ?? null, {
    unreadViews,
    unreadActivities: turnActivities,
  });
  const { agentId } = useParams<{ agentId: string }>();
  // The transcript's initial window needs only the URL's agent id: fetch it
  // in parallel with the agent row instead of a round-trip behind it —
  // `AgentStreamView` mounts once the agent resolves and its stream hook
  // dedupes onto this in-flight request.
  usePrefetchAgentStream(agentId);
  const navigate = useNavigate();
  const location = useLocation();
  const panel = useTwoPanelState();
  const routes = useOrgRoutes();
  // With the sessions dock active (desktop `sessions` nav style), the app
  // shell already renders the session list as its sidebar; this page skips
  // its own list and hands the sidebar-toggle affordance to the dock panel.
  const [navStyle] = useNavStyle();
  const isDesktop = useIsDesktop();
  const navPanel = useNavPanel();
  const dockActive = isDesktop && navStyle === 'sessions';
  const launchRouteState = useMemo(() => {
    const parsed = LaunchAgentRouteStateSchema.safeParse(location.state);
    return parsed.success ? parsed.data : null;
  }, [location.state]);
  const launchAttachmentDefaults = useMemo(() => {
    const attachments = launchRouteState?.attachments;
    if (!attachments?.length) return undefined;
    return Object.fromEntries(
      attachments.map((attachment) => [attachment.attachmentId, attachment]),
    ) as Record<string, PluginAttachmentValue>;
  }, [launchRouteState]);

  const [eventIdParam, setEventIdParam] = useQueryState('eventId');
  // The launch dialog is driven purely by the react-router URL — never by nuqs.
  // A single synchronous source of truth means no throttled writer can flip the
  // dialog back open a tick after a launch has navigated into the new agent.
  // See `launch-dialog-url.ts` for why. `setLaunchDialogOpen` still merges onto
  // `location.search`, so it depends on nuqs writes reaching the router — see
  // `lib/nuqs-adapter-options.ts`.
  const launchDialogOpen = isLaunchDialogRequested(location.search);
  const draftParam = launchDialogDraftId(location.search);
  const groupParam = launchDialogGroupId(location.search);

  const setLaunchDialogOpen = useCallback(
    (open: boolean) => {
      navigate(
        {
          pathname: location.pathname,
          search: open
            ? withLaunchDialogQuery(location.search)
            : stripLaunchDialogQuery(location.search),
        },
        { replace: true },
      );
    },
    [navigate, location.pathname, location.search],
  );

  // The command palette navigates here with `state.openLaunchDialog` instead
  // of a `?new=true` query param because the closing CommandDialog (Radix
  // Dialog) can race with the opening LaunchAgentDialog and dismiss it
  // immediately. Location state avoids the URL-param timing issue. Translate
  // that one-shot signal into the canonical `?new=true` URL. `setLaunchDialogOpen`
  // navigates with `replace` and no location state, which also clears
  // `openLaunchDialog` so a back-navigation can't reopen the dialog.
  useEffect(() => {
    if (launchRouteState?.openLaunchDialog) {
      setLaunchDialogOpen(true);
    }
  }, [launchRouteState, setLaunchDialogOpen]);

  const highlightEventId = eventIdParam ?? undefined;

  const handleHighlightConsumed = useCallback(() => {
    if (eventIdParam) {
      void setEventIdParam(null, { history: 'replace' });
    }
  }, [eventIdParam, setEventIdParam]);

  const {
    data: allAgents = [],
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    pagesLoaded,
  } = useAgents(agentFilters.serverFilters);
  const agents = agentFilters.filterAgents(allAgents);
  // This page reads the same paginated list but has no scroll sentinel of its
  // own, so without this a filter the server cannot apply is answered from
  // page one alone.
  useAgentListCompletion({
    enabled: agentFilters.clientFilterActive,
    matchedCount: agents.length,
    pagesLoaded,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const activeAgents = useMemo(() => getNonArchivedAgents(agents), [agents]);

  const listMatch = agentId ? (agents.find((a) => a.id === agentId) ?? null) : null;
  const {
    data: directAgent,
    error: directAgentError,
    isFetching: directAgentFetching,
  } = useAgent(listMatch ? undefined : agentId);
  const freshAgent = listMatch ?? directAgent ?? null;
  const lookupStatus = agentId
    ? resolveAgentLookup(freshAgent, directAgentError, directAgentFetching)
    : 'none';

  // Opening an agent counts as "looked at": clear its unseen-changes dot in the
  // sidebar. We record a view both when the agent opens and when it stops being
  // the open one (the effect cleanup), so activity that streams in while you
  // watch it live is covered too — the leave write stamps the latest time. The
  // open row hides its own dot regardless (see ActiveAgentRow), so these two
  // writes per session are all that's needed and never spam per activity tick.
  // The two writes are tagged `open` / `leave` because they aren't the same
  // thing: "mark as unread" on the open agent must survive the leave write.
  const { record: recordAgentView } = useRecordAgentView();
  const openAgentId = freshAgent?.id ?? null;
  useEffect(() => {
    if (!openAgentId) return;
    recordAgentView(openAgentId, 'open');
    return () => recordAgentView(openAgentId, 'leave');
  }, [openAgentId, recordAgentView]);

  const pendingLaunchFromState = launchRouteState?.openLaunchDialog === true;
  // Landing on the index normally opens the most recent run. A navigation that
  // asks for the empty state opts out — deleting the open agent sends the
  // viewer here, and re-opening the next run would undo the delete's whole
  // point (see `agents-index-route-state`).
  const skipAutoSelect = shouldSkipAutoSelect(location.state);
  useEffect(() => {
    if (skipAutoSelect) return;
    if (!agentId && activeAgents.length > 0 && !launchDialogOpen && !pendingLaunchFromState) {
      navigate({ pathname: routes.agent(activeAgents[0].id), search: '' }, { replace: true });
    }
  }, [
    agentId,
    activeAgents,
    navigate,
    routes,
    launchDialogOpen,
    pendingLaunchFromState,
    skipAutoSelect,
  ]);

  // Leaving the open run lands straight on the run that replaces it. Going via
  // the index instead paints the dashboard for a frame before the auto-select
  // effect above redirects into that same run — the whole content pane
  // flashing past on the way to where the viewer was always going to end up.
  // `next` comes from the sidebar, which knows the visual order; the fallback
  // covers callers that don't (the stream view's own archive control).
  const handleDeselect = useCallback(
    (next?: Agent) => {
      const target = next ?? activeAgents.find((a) => a.id !== agentId);
      navigate(target ? { pathname: routes.agent(target.id), search: '' } : routes.runs);
    },
    [activeAgents, agentId, navigate, routes],
  );

  const handleSelect = useCallback(
    (agent: Agent) => {
      // Drop any inherited search params (e.g. `?tab=code-review`) so the
      // new agent opens on its default chat view. Globally-registered
      // plugin tabs are valid for every agent in the org, which means the
      // self-heal effect in `AgentStreamView` doesn't fire to clear them
      // — and a plugin tab rendered against an agent with no relevant
      // data shows up as a black pane. Forcing `search: ''` here is the
      // belt-and-braces companion to the in-view fallback added in #1002.
      // Mounting the agent stream view is heavy; mark the navigation as a
      // transition so the mount stays interruptible and never blocks the
      // just-finished launch-dialog close animation.
      startTransition(() => {
        navigate({ pathname: routes.agent(agent.id), search: '' });
      });
      panel.closeSidebar();
    },
    [navigate, panel, routes],
  );

  // Group and label chips render deep inside rows and stream headers; hand
  // them the list's filters so "Filter to this" works from wherever the chip
  // is shown. The filters themselves are shared per tab through their
  // storage key, so this provider and the sidebar's own instance stay in step.
  const listFilterActions = useMemo(
    () => ({
      activeGroupId: agentFilters.filters.groupId,
      setGroupFilter: agentFilters.setGroupId,
      activeLabelId: agentFilters.filters.labelId,
      setLabelFilter: agentFilters.setLabelId,
    }),
    [
      agentFilters.filters.groupId,
      agentFilters.setGroupId,
      agentFilters.filters.labelId,
      agentFilters.setLabelId,
    ],
  );

  const handleOpenLaunchDialog = useCallback(
    () => setLaunchDialogOpen(true),
    [setLaunchDialogOpen],
  );

  // The sidebar's per-group "+". Clears any `draft` in the same navigation:
  // a draft carries its own remembered group, so opening one *and* forcing a
  // different group would show the user two answers to the same question.
  const handleLaunchInGroup = useCallback(
    (group: SidebarSectionContext) => {
      navigate(
        {
          pathname: location.pathname,
          search: withLaunchDialogQuery(location.search, null, group.id),
        },
        { replace: true },
      );
    },
    [navigate, location.pathname, location.search],
  );

  const handleSelectDraft = useCallback(
    (draft: AgentDraft) => {
      // Open the dialog and preload the draft in one navigation so the `?new`
      // and `?draft` params always land together. Any `?group` from a previous
      // "+" is dropped: the draft remembers its own group, and a forced one
      // would silently overrule what the draft was saved with.
      navigate(
        {
          pathname: location.pathname,
          search: withLaunchDialogQuery(location.search, draft.id, null),
        },
        { replace: true },
      );
    },
    [navigate, location.pathname, location.search],
  );

  const toggleSidebar = dockActive
    ? (navPanel?.toggle ?? panel.toggleSidebar)
    : panel.toggleSidebar;

  // One branch per outcome of the URL's agent id. `notFound` and `error`
  // deliberately do not fall through to the launch placeholder: a failed
  // lookup that renders "Launch an agent" is indistinguishable from an org
  // with no runs at all.
  function renderDetail() {
    if (freshAgent) {
      return (
        <AgentStreamErrorBoundary key={freshAgent.id}>
          <AgentStreamView
            agent={freshAgent}
            onArchived={handleDeselect}
            onToggleSidebar={toggleSidebar}
            highlightEventId={highlightEventId}
            onHighlightConsumed={handleHighlightConsumed}
          />
        </AgentStreamErrorBoundary>
      );
    }
    if (!agentId) {
      return (
        <AgentDashboard onToggleSidebar={toggleSidebar} onLaunchAgent={handleOpenLaunchDialog} />
      );
    }
    switch (lookupStatus) {
      case 'noAccess':
        return <AgentNoAccessState agentId={agentId} />;
      case 'notFound':
        return <AgentNotFoundState agentId={agentId} />;
      case 'error':
        return (
          <ErrorState className="h-full content-center" message="Failed to load this session." />
        );
      default:
        return <LoadingState className="h-full" message="Loading session…" />;
    }
  }

  const content = (
    /*
      Keyed wrapper around the agent stream view. We deliberately avoid
      wrapping this in `AnimatePresence` + `motion.div`: switching from a
      non-default plugin tab (e.g. `?tab=code-review`) to a different
      agent fired a cascade of unmount-time cleanups (nuqs subscriptions,
      plugin tab hooks like `useReview`/`useScrollMemory`, etc.) which
      interfered with motion's exit promise. The new agent's `motion.div`
      would mount with `initial={{ opacity: 0 }}` but the `animate`
      transition to `opacity: 1` never fired — surfacing as a blank pane
      with no errors in the console. Agent switching is a state change,
      not an animation; a plain keyed div is enough.
    */
    <div key={freshAgent?.id ?? 'dashboard'} className="flex h-full min-h-0 flex-col">
      {renderDetail()}
    </div>
  );

  if (dockActive) {
    return (
      <AgentListFilterContext.Provider value={listFilterActions}>
        <div className="flex h-full min-h-0 flex-col overflow-hidden">{content}</div>
        <LaunchAgentDialog
          open={launchDialogOpen}
          onOpenChange={setLaunchDialogOpen}
          onSuccess={handleSelect}
          draftId={draftParam}
          attachmentDefaults={launchAttachmentDefaults}
        />
      </AgentListFilterContext.Provider>
    );
  }

  return (
    <AgentListFilterContext.Provider value={listFilterActions}>
      <TwoPanelLayout
        autoSaveId="agents-sidebar"
        sidebarCollapsed={panel.collapsed}
        onSidebarCollapsedChange={panel.setCollapsed}
        collapsedSidebarWidth={0}
        sidebar={
          <AgentSidebar
            selectedId={freshAgent?.id ?? null}
            selectedDraftId={draftParam}
            onSelect={handleSelect}
            onSelectDraft={handleSelectDraft}
            onDeselect={handleDeselect}
            onToggle={panel.toggleCollapsed}
            onLaunchAgent={handleOpenLaunchDialog}
            onLaunchInGroup={handleLaunchInGroup}
          />
        }
        mobileSidebar={
          <AgentSidebar
            selectedId={freshAgent?.id ?? null}
            selectedDraftId={draftParam}
            onSelect={handleSelect}
            onSelectDraft={handleSelectDraft}
            onDeselect={handleDeselect}
            onToggle={panel.closeSidebar}
            onLaunchAgent={handleOpenLaunchDialog}
            onLaunchInGroup={handleLaunchInGroup}
            mobile
          />
        }
        sidebarOpen={panel.sidebarOpen}
        onSidebarOpenChange={panel.setSidebarOpen}
      >
        {content}
      </TwoPanelLayout>

      <LaunchAgentDialog
        open={launchDialogOpen}
        onOpenChange={setLaunchDialogOpen}
        onSuccess={handleSelect}
        draftId={draftParam}
        attachmentDefaults={launchAttachmentDefaults}
        groupDefault={groupParam}
      />
    </AgentListFilterContext.Provider>
  );
}
